import React, {useCallback, useEffect, useRef, useState} from "react";
import {LookDataType} from "./LookTypes";
import {useMediaQuery} from "react-responsive";
import TitleBlock from "./TitleBlock";
import TagsBlock from "./TagsBlock";
import {queryBreakpointSmall, queryBreakpointLarge} from "./stlHelper";

declare const c: {
  throttle: <T = () => void>(func: T, wait: number) => T;
};

// Line clamp of description. Hardcoded bc it's necessary for some height calculation
const DESC_LINE_CLAMP_DESKTOP = 8;
const DESC_LINE_CLAMP_MOBILE = 3;
// 16px * 1.5 (font-size * line-height, as inherited from style `.stl-container .description {...}`)
const lineHeight = 24;

const LookInfoSection: React.FC<{look: LookDataType; isLookDetailPageItem: boolean}> = ({
  look,
  isLookDetailPageItem,
}) => {
  const [isTextTruncated, setIsTextTruncated] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const isMobile =
    (isLookDetailPageItem && useMediaQuery({query: queryBreakpointSmall})) ||
    (!isLookDetailPageItem && useMediaQuery({query: queryBreakpointLarge}));

  // Setting of window.innerWidth
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // region Prüfen ob Text abgeschnitten wurde

  const checkTruncation = useCallback(
    c.throttle(() => {
      if (descriptionRef.current) {
        const element = descriptionRef.current;

        // Temporär die Ellipsis-Eigenschaften entfernen
        const originalStyle = {
          webkitLineClamp: element.style.webkitLineClamp,
          overflow: element.style.overflow,
          display: element.style.display,
        };

        element.style.webkitLineClamp = "unset";
        element.style.overflow = "visible";
        element.style.display = "block";

        // Tatsächliche Höhe messen
        const fullHeight = element.scrollHeight;

        // Styles zurücksetzen
        element.style.webkitLineClamp = originalStyle.webkitLineClamp;
        element.style.overflow = originalStyle.overflow;
        element.style.display = originalStyle.display;

        // Prüfen ob der Text länger ist als DESC_LINE_CLAMP Zeilen
        const DESC_LINE_CLAMP = isMobile ? DESC_LINE_CLAMP_MOBILE : DESC_LINE_CLAMP_DESKTOP;
        const maxHeight = lineHeight * DESC_LINE_CLAMP;

        setIsTextTruncated(fullHeight > maxHeight);
      }
    }, 400),
    [isMobile],
  );

  useEffect(() => {
    checkTruncation();
  }, [look.description, windowWidth, isMobile, descriptionRef?.current?.scrollHeight]);

  // endregion Prüfen ob Text abgeschnitten wurde

  const webkitLineClamp = isMobile ? DESC_LINE_CLAMP_MOBILE : DESC_LINE_CLAMP_DESKTOP;

  return (
    <div className="info-section">
      <TitleBlock look={look} isMobile={false} isLookDetailPageItem={isLookDetailPageItem} />
      <TagsBlock look={look} isMobile={false} />
      <div className="description">
        <div
          ref={descriptionRef}
          className={"description-text"}
          style={isDescriptionExpanded ? {overflow: "visible"} : {WebkitLineClamp: webkitLineClamp}}
        >
          <i>{`„${look.description}“`}</i>
        </div>
        {isTextTruncated && (
          <button
            className="read-more"
            onClick={() => {
              setIsDescriptionExpanded(!isDescriptionExpanded);
            }}
          >
            {isDescriptionExpanded ? "einklappen" : "weiterlesen"}
          </button>
        )}
      </div>
    </div>
  );
};

export default LookInfoSection;
